import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { 
  Play,
  MessageSquare,
  Mic,
  MousePointer,
  Calendar,
  Filter,
  Clock,
  User,
  Globe,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Video,
  CheckCircle,
  Trash2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { API_URL } from '../config/api';
import { useAuth } from '@/context/auth-context';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import ChatSessionReplay from '../components/ChatSessionReplay';
import OpenReplayViewer from '../components/OpenReplayViewer';
import { OpenReplaySession } from '../components/OpenReplaySession';
import { SessionReplayViewer } from '../components/SessionReplayViewer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface Session {
  id: string;
  sessionId: string;
  userId?: string;
  userEmail?: string;
  siteUrl: string;
  siteName?: string;
  startTime: string;
  duration: number;
  pageViews: number;
  interactions: {
    chatbot: boolean;
    voicebot: boolean;
    suggestions: boolean;
    clicks: number;
  };
  device?: {
    browser?: string;
    os?: string;
    type?: string;
  };
  location?: {
    country?: string;
    city?: string;
  };
  openReplaySessionId?: string;
  openReplaySessionIdType?: 'numeric' | 'string' | 'unknown';
  canGenerateShareToken?: boolean;
  openReplayUrl?: string;
  openReplayProjectId?: string;
  openReplayProjectKey?: string;
  publicReplayUrl?: string;
  embeddedReplayUrl?: string;
  hasVideo?: boolean;
  videoUrl?: string;
  screenshotUrl?: string;
  lastScreenshotUrl?: string;
  defaultTab?: 'video' | 'chat';
}

// Fetches a thumbnail via authFetch and renders it as a blob URL to avoid JWT in <img src>.
// Falls back to fallbackSrc if the authenticated fetch fails.
function ThumbnailBlobImg({ thumbnailPath, fallbackSrc, alt, className }: {
  thumbnailPath: string;
  fallbackSrc?: string;
  alt: string;
  className?: string;
}) {
  const [blobState, setBlobState] = useState<{ blobUrl: string; forPath: string } | null>(null);
  const [failedPath, setFailedPath] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setBlobState(null);
    setFailedPath(null);
    const targetPath = thumbnailPath;
    authFetch(targetPath)
      .then(r => {
        if (!r.ok) throw new Error(`Thumbnail fetch failed: ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobState({ blobUrl: objectUrl, forPath: targetPath });
      })
      .catch(() => {
        if (!cancelled) setFailedPath(targetPath);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [thumbnailPath]);

  // Only use cached blob/failed state when it belongs to the current thumbnailPath.
  const blobUrl = blobState?.forPath === thumbnailPath ? blobState.blobUrl : null;
  const failed = failedPath === thumbnailPath;
  const src = blobUrl || (failed && fallbackSrc) || undefined;
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={(e) => {
        if (fallbackSrc && e.currentTarget.src !== fallbackSrc) {
          e.currentTarget.src = fallbackSrc;
        } else {
          e.currentTarget.style.display = 'none';
        }
      }}
    />
  );
}

export default function SessionReplays() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [isReplayOpen, setIsReplayOpen] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState<{ [key: string]: 'idle' | 'recording' | 'checking' }>({});
  const [recordingProgress, setRecordingProgress] = useState<{ [key: string]: any }>({});
  
  // Filters
  const [dateRange, setDateRange] = useState('90days'); // Default to last 90 days
  const [interactionFilter, setInteractionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyOpenReplay, setOnlyOpenReplay] = useState(false); // Show all sessions by default
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [videoBlobState, setVideoBlobState] = useState<{ blobUrl: string; forVideoUrl: string } | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  // Only use the cached blob when it belongs to the currently selected session —
  // prevents stale blob from a previous session appearing briefly before the
  // effect fires after a selection change.
  const videoBlobUrl = videoBlobState?.forVideoUrl === selectedSession?.videoUrl ? videoBlobState.blobUrl : null;
  const sessionsPerPage = 20;

  // Fetch sessions when filters change
  useEffect(() => {
    fetchSessions();
  }, [selectedSite, dateRange, interactionFilter, currentPage, onlyOpenReplay]);

  // Track page visit for onboarding
  useEffect(() => {
    if (selectedSite?._id) {
      authFetch('/onboarding/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite._id,
          activity: {
            type: 'experiences_visited',
            path: '/session-replays'
          }
        })
      }).catch(err => console.error('Failed to track experiences visit:', err));
    }
  }, [selectedSite]);

  // Fetch video as blob when a session with video is selected.
  // Avoids putting JWT in a URL. Uses a cancelled flag to discard stale responses
  // when the selected session changes while a prior fetch is in flight.
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (selectedSession?.hasVideo && selectedSession?.videoUrl) {
      setVideoBlobState(null);
      setVideoError(null);
      const targetVideoUrl = selectedSession.videoUrl;
      authFetch(`/api${targetVideoUrl}`)
        .then(r => {
          if (!r.ok) throw new Error(`Video unavailable (${r.status})`);
          return r.blob();
        })
        .then(blob => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setVideoBlobState({ blobUrl: objectUrl, forVideoUrl: targetVideoUrl });
        })
        .catch(err => {
          if (cancelled) return;
          console.error('[SessionReplays] Failed to load video blob:', err);
          setVideoBlobState(null);
          setVideoError(err.message || 'Failed to load video');
        });
    } else {
      setVideoBlobState(null);
      setVideoError(null);
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedSession?.videoUrl]);

  // Site selection is now handled by global context

  const fetchSessions = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      let startDate, endDate = new Date();
      switch (dateRange) {
        case '1day':
          startDate = subDays(new Date(), 1);
          break;
        case '7days':
          startDate = subDays(new Date(), 7);
          break;
        case '15days':
          startDate = subDays(new Date(), 15);
          break;
        case '30days':
          startDate = subDays(new Date(), 30);
          break;
        default:
          startDate = subDays(new Date(), 7);
      }

      const params = new URLSearchParams({
        siteId: selectedSite ? (selectedSite._id || selectedSite.id) : 'all',
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString(),
        interactionFilter,
        page: currentPage.toString(),
        limit: sessionsPerPage.toString(),
        onlyOpenReplay: onlyOpenReplay.toString(),
      });

      const response = await fetch(`${API_URL}/sessions/merged?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }
      
      const data = await response.json();
      setSessions(data.sessions || []);
      setTotalPages(Math.ceil((data.total || 0) / sessionsPerPage));
      
      // Check video status only for videos requested in this browser session
      checkVideoStatuses(data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sessions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Check video status for all sessions with OpenReplay data
  const checkVideoStatuses = async (sessionsList: Session[]) => {
    const sessionsToCheck = sessionsList.filter(s => {
      // Check all sessions that have OpenReplay data
      return s.openReplaySessionId && s.openReplayProjectKey;
    });
    
    for (const session of sessionsToCheck) {
      try {
        const checkResponse = await authFetch(
          `/api/sessions/video/${session.openReplayProjectKey}/${session.openReplaySessionId}`,
          { method: 'HEAD' }
        );

        const sessionKey = session.sessionId;

        if (checkResponse.status === 200) {
          // Video is ready
          setSessions(prevSessions => 
            prevSessions.map(s => 
              s.sessionId === session.sessionId 
                ? { ...s, hasVideo: true, videoUrl: `/sessions/video/${session.openReplayProjectKey}/${session.openReplaySessionId}` }
                : s
            )
          );
          // Clear the session storage flag
          sessionStorage.removeItem(`video-requested-${session.sessionId}`);
        } else if (checkResponse.status === 202) {
          // Video is processing
          setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'recording' }));
          // Start checking for this video
          startVideoCheck(session);
        }
        // 404 means not requested, which is the default state
      } catch (err) {
        // Ignore errors in status check
      }
    }
  };

  // Extract the video checking logic into a reusable function
  const startVideoCheck = (session: Session) => {
    const sessionKey = session.sessionId;
    let checkInterval: NodeJS.Timeout;
    
    checkInterval = setInterval(async () => {
      try {
        const checkResponse = await authFetch(
          `/api/sessions/video/${session.openReplayProjectKey}/${session.openReplaySessionId}`,
          { method: 'HEAD' }
        );

        if (checkResponse.status === 200) {
          clearInterval(checkInterval);
          setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'idle' }));
          
          // Update session to show it has video
          setSessions(prevSessions => 
            prevSessions.map(s => 
              s.sessionId === session.sessionId 
                ? { ...s, hasVideo: true, videoUrl: `/sessions/video/${session.openReplayProjectKey}/${session.openReplaySessionId}` }
                : s
            )
          );
          
          // Clear the session storage flag
          sessionStorage.removeItem(`video-requested-${session.sessionId}`);
        } else if (checkResponse.status === 404) {
          // Video was deleted or error occurred
          clearInterval(checkInterval);
          setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'idle' }));
        }
      } catch (err) {
        // Continue checking
      }
    }, 10000); // Check every 10 seconds

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(checkInterval);
      setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'idle' }));
    }, 300000);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getInteractionBadges = (interactions: Session['interactions']) => {
    const badges = [];
    
    if (interactions.chatbot) {
      badges.push(
        <Badge key="chatbot" variant="default" className="bg-blue-500">
          <MessageSquare className="w-3 h-3 mr-1" />
          Chatbot
        </Badge>
      );
    }
    
    if (interactions.voicebot) {
      badges.push(
        <Badge key="voicebot" variant="default" className="bg-purple-500">
          <Mic className="w-3 h-3 mr-1" />
          Voicebot
        </Badge>
      );
    }
    
    if (interactions.suggestions) {
      badges.push(
        <Badge key="suggestions" variant="default" className="bg-green-500">
          <MousePointer className="w-3 h-3 mr-1" />
          Suggestions
        </Badge>
      );
    }
    
    return badges;
  };

  const filteredSessions = sessions.filter(session => {
    // Apply OpenReplay filter
    if (onlyOpenReplay && !session.openReplaySessionId) {
      return false;
    }
    
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        session.userEmail?.toLowerCase().includes(query) ||
        session.sessionId.toLowerCase().includes(query) ||
        session.siteUrl.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const openReplay = (session: Session, defaultTab?: 'video' | 'chat') => {
    setSelectedSession({ ...session, defaultTab });
    setIsReplayOpen(true);
  };

  const recordVideo = async (session: Session) => {
    if (!session.openReplaySessionId || !session.openReplayProjectKey) {
      toast({
        title: 'Error',
        description: 'Missing session information for recording',
        variant: 'destructive',
      });
      return;
    }

    const sessionKey = session.sessionId;
    setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'recording' }));

    try {
      // Map project key to numeric ID (OpenReplay uses numeric IDs for URL navigation)
      const projectKeyToId: Record<string, string> = {
        'nRNt8yxBpGkdKMIHfMXx': '15', // Toy Store Local
      };

      const numericProjectId = session.openReplayProjectId ||
                                projectKeyToId[session.openReplayProjectKey] ||
                                session.openReplayProjectKey;

      const requestBody = {
        sessionId: session.openReplaySessionId,
        projectId: numericProjectId, // Numeric ID for OpenReplay URL navigation
        siteId: `pagemind-project${session.openReplayProjectKey}`,
        duration: session.duration * 1000, // Convert to milliseconds
      };
      
      console.log('[Record Video] Sending request:', requestBody);
      
      // Trigger video recording via the backend API
      const response = await authFetch('/sessions/record-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Record video error:', response.status, errorText);
        throw new Error(`Failed to start recording: ${response.status} ${errorText}`);
      }

      // Set status to 'checking' immediately after successful request
      setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'checking' }));

      toast({
        title: 'Video Request Received',
        description: 'Your replay video is being generated. This may take a few minutes.',
      });

      // After a delay, change status to 'recording' to show progress
      setTimeout(() => {
        setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'recording' }));
      }, 3000);

      // Mark this session as having a video request in progress
      // This helps maintain state across page reloads
      sessionStorage.setItem(`video-requested-${session.sessionId}`, 'true');
      
      // Start polling for progress
      let progressInterval: NodeJS.Timeout;
      let checkInterval: NodeJS.Timeout;
      let progressCheckCount = 0;
      const maxProgressChecks = 60; // Check for up to 5 minutes (60 * 5 seconds)
      
      progressInterval = setInterval(async () => {
        progressCheckCount++;
        try {
          const progressResponse = await authFetch(`/sessions/recording-progress/${session.openReplaySessionId}`);
          
          if (progressResponse.status === 304) {
            // 304 Not Modified - progress hasn't changed, continue checking
            console.log('Recording progress unchanged (304), continuing to check...');
          } else if (progressResponse.status === 403 || progressResponse.status === 404) {
            // Session not resolvable via session_tracking (OpenReplay-only session or
            // session not yet tracked). Skip progress channel and go straight to video check.
            console.log(`Recording progress returned ${progressResponse.status}, switching to video status check`);
            clearInterval(progressInterval);
            startCheck();
          } else if (progressResponse.ok) {
            const progress = await progressResponse.json();
            setRecordingProgress(prev => ({ ...prev, [sessionKey]: progress }));
            
            // Only stop checking if we explicitly get inProgress: false
            if (progress.inProgress === false) {
              // Progress explicitly indicates recording is done
              clearInterval(progressInterval);
              startCheck();
            }
          }
          
          // Transition to video checking after max attempts
          if (progressCheckCount >= maxProgressChecks) {
            console.log('Max progress checks reached, transitioning to video status check');
            clearInterval(progressInterval);
            startCheck();
          }
        } catch (error) {
          console.error('Error checking recording progress:', error);
        }
      }, 5000); // Check every 5 seconds

      // Use the reusable startVideoCheck function
      const startCheck = () => {
        // Keep showing "Replay In Process" while checking video status
        // Don't change to 'checking' which shows "Replay Requested"
        startVideoCheck(session);
      };

    } catch (error) {
      console.error('Error recording video:', error);
      setRecordingStatus(prev => ({ ...prev, [sessionKey]: 'idle' }));
      toast({
        title: 'Error',
        description: 'Failed to start video recording',
        variant: 'destructive',
      });
    }
  };

  const deleteVideo = async (session: Session) => {
    if (!session.openReplaySessionId || !session.openReplayProjectKey) {
      toast({
        title: 'Error',
        description: 'Missing session information for deleting video',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await authFetch(
        `/sessions/video/${session.openReplayProjectKey}/${session.openReplaySessionId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        // Update session to show it no longer has video
        setSessions(prevSessions => 
          prevSessions.map(s => 
            s.sessionId === session.sessionId 
              ? { ...s, hasVideo: false, videoUrl: undefined }
              : s
          )
        );

        toast({
          title: 'Video Deleted',
          description: 'The video has been deleted. You can now request a new recording.',
        });
      } else {
        throw new Error('Failed to delete video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete video',
        variant: 'destructive',
      });
    }
  };


  return (
    <TooltipProvider>
      <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Session Replays</h1>
        <p className="text-muted-foreground mt-1">
          Watch recorded sessions and analyze user interactions with PageMind features
        </p>
      </div>

      {/* Site Information */}
      {selectedSite && (
        <Card className="mb-4">
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-lg font-medium">
                  {selectedSite.name || selectedSite.url}
                </CardTitle>
                <CardDescription className="text-sm">
                  Viewing sessions for this site
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <Label htmlFor="date-range">Date Range</Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger id="date-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1day">Last 24 hours</SelectItem>
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="15days">Last 15 days</SelectItem>
                  <SelectItem value="30days">Last 30 days</SelectItem>
                  <SelectItem value="90days">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Interaction Filter */}
            <div>
              <Label htmlFor="interaction-filter">Interactions</Label>
              <Select value={interactionFilter} onValueChange={setInteractionFilter}>
                <SelectTrigger id="interaction-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sessions</SelectItem>
                  <SelectItem value="with-pagemind">With PageMind interactions</SelectItem>
                  <SelectItem value="without-pagemind">Without PageMind interactions</SelectItem>
                  <SelectItem value="chatbot">Used Chatbot</SelectItem>
                  <SelectItem value="voicebot">Used Voicebot</SelectItem>
                  <SelectItem value="suggestions">Used Suggestions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* OpenReplay Filter */}
            <div>
              <Label htmlFor="openreplay-filter">OpenReplay Sessions</Label>
              <div className="flex items-center space-x-2 mt-2">
                <Switch
                  id="openreplay-filter"
                  checked={onlyOpenReplay}
                  onCheckedChange={setOnlyOpenReplay}
                />
                <Label htmlFor="openreplay-filter" className="text-sm text-muted-foreground">
                  Only show sessions with replay data
                </Label>
              </div>
            </div>

            {/* Search */}
            <div>
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Email, session ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sessions</CardTitle>
              <CardDescription>
                Click on any session to watch the replay
              </CardDescription>
            </div>
            {onlyOpenReplay && (
              <Badge variant="outline" className="bg-green-50 border-green-200">
                OpenReplay sessions only
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No sessions found matching your criteria</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Pages</TableHead>
                    <TableHead>Interactions</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSessions.map((session) => (
                    <TableRow key={session.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          {(session.hasVideo || session.screenshotUrl || session.lastScreenshotUrl) && (
                            <div 
                              className="relative w-32 h-20 overflow-hidden rounded border border-border cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => session.hasVideo && openReplay(session, 'video')}
                            >
                              {session.hasVideo ? (
                                <ThumbnailBlobImg
                                  thumbnailPath={`/api/sessions/thumbnail/${session.openReplayProjectKey}/${session.openReplaySessionId}`}
                                  fallbackSrc={session.screenshotUrl || session.lastScreenshotUrl ? `${API_URL}${session.screenshotUrl || session.lastScreenshotUrl}` : undefined}
                                  alt="Video thumbnail"
                                  className="absolute inset-0 w-full h-full object-cover object-top"
                                />
                              ) : (
                                <img
                                  src={`${API_URL}${session.screenshotUrl || session.lastScreenshotUrl}`}
                                  alt="Session screenshot"
                                  className="absolute inset-0 w-full h-full object-cover object-top"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                              {session.hasVideo && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Play className="h-8 w-8 text-white drop-shadow-lg" />
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(session.startTime), 'MMM d, HH:mm')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm">{session.userEmail || 'Anonymous'}</p>
                            {session.location && (
                              <p className="text-xs text-muted-foreground">
                                {session.location.city}, {session.location.country}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Globe className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-semibold">{session.siteName || 'Unknown Site'}</p>
                                <p className="text-xs">{session.siteUrl || 'No URL available'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span className="text-sm truncate max-w-[200px]">
                            {session.siteName || session.siteUrl || 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatDuration(session.duration)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{session.pageViews}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {getInteractionBadges(session.interactions)}
                          {session.interactions.clicks > 0 && (
                            <Badge variant="outline">
                              {session.interactions.clicks} clicks
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{session.device?.browser}</p>
                          <p className="text-xs text-muted-foreground">{session.device?.os}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          {/* First row: View Chat button */}
                          {(session.openReplaySessionId || session.interactions?.chatbot) && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => openReplay(session, 'chat')}
                              className="w-full justify-start text-xs min-w-[140px]"
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              View Chat
                            </Button>
                          )}
                          
                          {/* Second row: Request Replay or View Replay button */}
                          {session.openReplaySessionId && (
                            <div className="flex gap-2 items-center">
                              {!session.hasVideo ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => recordVideo(session)}
                                  disabled={recordingStatus[session.sessionId] !== 'idle' && recordingStatus[session.sessionId] !== undefined}
                                  className="w-full justify-start text-xs min-w-[140px]"
                                >
                                  {recordingStatus[session.sessionId] === 'recording' && (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                      Replay In Process
                                    </>
                                  )}
                                  {recordingStatus[session.sessionId] === 'checking' && (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Replay Requested
                                    </>
                                  )}
                                  {(!recordingStatus[session.sessionId] || recordingStatus[session.sessionId] === 'idle') && (
                                    <>
                                      <Video className="h-4 w-4 mr-1" />
                                      Request Replay
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => openReplay(session, 'video')}
                                    className="flex-1 justify-start text-xs min-w-[140px]"
                                  >
                                    <Play className="h-4 w-4 mr-1" />
                                    View Replay
                                  </Button>
                                  
                                  {/* Delete video button for super admins */}
                                  {user?.role === 'super_admin' && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => deleteVideo(session)}
                                          className="h-8 w-8 p-0"
                                        >
                                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Delete video</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* Show recording progress screenshots */}
                          {recordingProgress[session.sessionId] && recordingProgress[session.sessionId].screenshots && recordingProgress[session.sessionId].screenshots.length > 0 && (
                            <div className="mt-2 flex gap-2 overflow-x-auto">
                              {recordingProgress[session.sessionId].screenshots.map((screenshot: any) => (
                                <div key={screenshot.index} className="flex-shrink-0">
                                  <img 
                                    src={`${API_URL}${screenshot.url}`}
                                    alt={`Recording progress ${screenshot.index}`}
                                    className="h-16 w-auto rounded border border-gray-200"
                                  />
                                  <p className="text-xs text-center mt-1 text-muted-foreground">
                                    Step {screenshot.index}/{recordingProgress[session.sessionId].total}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Replay Dialog */}
      <Dialog open={isReplayOpen} onOpenChange={setIsReplayOpen}>
        <DialogContent className="max-w-[90vw] h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Session Replay</DialogTitle>
            {selectedSession && (
              <DialogDescription className="flex items-center gap-4 mt-2">
                <span>{selectedSession.userEmail || 'Anonymous'}</span>
                <span>•</span>
                <span>{format(new Date(selectedSession.startTime), 'MMM d, yyyy HH:mm')}</span>
                <span>•</span>
                <span>{formatDuration(selectedSession.duration)}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {selectedSession && (
              <Tabs defaultValue={selectedSession.defaultTab || (selectedSession.hasVideo ? "video" : "chat")} className="h-full flex flex-col">
                <TabsList className="mx-4 mt-2">
                  {selectedSession.hasVideo && (
                    <TabsTrigger value="video">Visual Replay</TabsTrigger>
                  )}
                  <TabsTrigger value="chat">Chat Timeline</TabsTrigger>
                </TabsList>
                {selectedSession.hasVideo && (
                  <TabsContent value="video" className="flex-1 overflow-hidden p-0">
                    <div className="h-full bg-black flex items-center justify-center">
                      {videoError ? (
                        <p className="text-white/70 text-sm px-4 text-center">{videoError}</p>
                      ) : videoBlobUrl ? (
                        <video
                          controls
                          autoPlay
                          className="w-full h-full object-contain"
                          src={videoBlobUrl}
                        >
                          <source src={videoBlobUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <p className="text-white/50 text-sm">Loading video…</p>
                      )}
                    </div>
                  </TabsContent>
                )}
                <TabsContent value="chat" className="flex-1 overflow-y-auto px-4 pb-4">
                  <ChatSessionReplay 
                    sessionId={selectedSession.sessionId}
                    onClose={() => setIsReplayOpen(false)}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
    </TooltipProvider>
  );
}